/**
 * METRIC CARD
 * Reusable card for displaying single metrics with optional trend
 */

import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

export default function MetricCard({
  label,
  value,
  format = 'number',
  trend = null,  // { direction: 'up' | 'down', value: '12.5%' }
  icon = null,
  sparkline = null,  // Array of values for mini chart
  isLoading = false
}) {

  // Format value based on type
  const formattedValue = React.useMemo(() => {
    if (isLoading) return '...';
    if (value === null || value === undefined) return 'N/A';

    switch (format) {
      case 'currency':
        return `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'number':
        return parseFloat(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
      case 'percent':
        return `${parseFloat(value).toFixed(1)}%`;
      default:
        return value;
    }
  }, [value, format, isLoading]);

  return (
    <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-all border border-gray-200 hover:border-blue-300">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-2">{label}</p>
          {isLoading ? (
            <div className="space-y-2">
              <div className="h-8 w-32 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-3 w-20 bg-gray-100 rounded animate-pulse"></div>
            </div>
          ) : (
            <div className="flex items-baseline">
              <p className="text-3xl font-bold text-gray-900">{formattedValue}</p>
              {trend && (
                <span className={`ml-2 flex items-center text-sm font-medium ${
                  trend.direction === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {trend.direction === 'up' ? (
                    <ArrowUp className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowDown className="w-4 h-4 mr-1" />
                  )}
                  {trend.value}
                </span>
              )}
            </div>
          )}
        </div>
        {icon && (
          <div className="ml-4 flex-shrink-0">
            <div className={`rounded-lg p-3 ${isLoading ? 'bg-gray-100 animate-pulse' : 'bg-blue-50'}`}>
              {icon}
            </div>
          </div>
        )}
      </div>
      {sparkline && sparkline.length > 0 && !isLoading && (
        <div className="mt-4">
          <svg width="100%" height="40" className="text-blue-500">
            {/* Simple sparkline visualization */}
            {sparkline.map((val, idx) => {
              const maxVal = Math.max(...sparkline);
              const minVal = Math.min(...sparkline);
              const range = maxVal - minVal || 1;
              const height = ((val - minVal) / range) * 30;
              const x = (idx / (sparkline.length - 1)) * 100;
              return (
                <circle
                  key={idx}
                  cx={`${x}%`}
                  cy={40 - height}
                  r="2"
                  fill="currentColor"
                />
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
}
